class InitElevarDataLayerHelper {
  /**
   * Initialize the Elevar Data Layer Helper
   * @param {Object} options - Configuration options
   * @param {Object} options.userProperties - User properties for tracking
   * @param {Array} options.currentOrderProducts - Current order products
   */
  constructor({ userProperties, currentOrderProducts }) {
    try {
      // Ensure ElevarDataLayer exists
      if (typeof window !== 'undefined') {
        window.ElevarDataLayer = window.ElevarDataLayer || [];
      }

      // Safely handle sessionStorage
      let landingSite = window.location.pathname;
      try {
        if (window.sessionStorage) {
          if (!sessionStorage.landing_site) {
            sessionStorage.landing_site = landingSite;
          } else {
            landingSite = sessionStorage.landing_site;
          }
        }
      } catch (e) {
        console.warn('Session storage not available:', e);
      }

      // Set default properties
      this.userProperties = userProperties || {};
      this.currentOrderProducts = currentOrderProducts || [];
      this.currencyCode = (window.SPREE_CURRENCY || 'USD').toUpperCase();
      this.marketingLandingSite = landingSite;
    } catch (error) {
      console.error('Error initializing ElevarDataLayerHelper:', error);
      // Initialize with safe defaults
      this.userProperties = {};
      this.currentOrderProducts = [];
      this.currencyCode = 'USD';
      this.marketingLandingSite = window.location.pathname;
    }
  }

  /**
   * Push an event to the ElevarDataLayer
   * @param {string} eventName - Name of the event to push
   * @param {Object} additionalData - Additional data to include in the push
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushEvent(eventName, additionalData = {}) {
    try {
      if (!eventName || typeof eventName !== 'string') {
        throw new Error('Invalid event name');
      }

      const eventData = {
        event: eventName,
        user_properties: this.userProperties,
        ...additionalData
      };

      if (window.ElevarDataLayer) {
        window.ElevarDataLayer.push(eventData);
        return eventData;
      }
    } catch (error) {
      console.error('Error pushing event to ElevarDataLayer:', error);
    }
  }

  /**
   * Push a custom event to the ElevarDataLayer
   * @param {string} eventName - Name of the custom event
   * @param {Object} data - Custom event data
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushCustomEvent(eventName, data = {}) {
    try {
      if (!eventName || typeof eventName !== 'string') {
        throw new Error('Invalid event name');
      }

      const eventData = {
        event: eventName,
        ...data
      };

      if (window.ElevarDataLayer) {
        window.ElevarDataLayer.push(eventData);
        return eventData;
      }
    } catch (error) {
      console.error('Error pushing custom event to ElevarDataLayer:', error);
    }
  }

  /**
   * Push user data event
   * @param {Object} options - Options for user data
   * @param {number|string} options.cartTotal - Total value of the cart
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushUserData({ cartTotal = '0' } = {}) {
    try {
      return this.pushEvent('dl_user_data', {
        cart_total: String(cartTotal),
        ecommerce: {
          currencyCode: this.currencyCode,
          cart_contents: {
            products: this.currentOrderProducts
          }
        }
      });
    } catch (error) {
      console.error('Error pushing user data:', error);
    }
  }

  /**
   * Push checkout flow event handler
   * @param {string} eventName - Checkout event name
   * @param {string} step - Checkout step number
   * @param {Object} additionalData - Additional data for the event
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushCheckoutFlow(eventName, step = '1', additionalData = {}) {
    try {
      return this.pushEvent(eventName, {
        ...additionalData,
        ecommerce: {
          currencyCode: this.currencyCode,
          checkout: {
            actionField: {
              step: String(step)
            },
            products: this.currentOrderProducts
          }
        }
      });
    } catch (error) {
      console.error('Error pushing checkout flow event:', error);
    }
  }

  /**
   * Push begin checkout event
   * @param {string} cartType - Type of cart (default: 'regular_checkout')
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushBeginCheckout(cartType = 'regular_checkout') {
    try {
      return this.pushCheckoutFlow('dl_begin_checkout', '1', { cart_type: cartType });
    } catch (error) {
      console.error('Error pushing begin checkout event:', error);
    }
  }

  /**
   * Push add shipping info event
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushAddShippingInfo() {
    try {
      return this.pushCheckoutFlow('dl_add_shipping_info', '2');
    } catch (error) {
      console.error('Error pushing add shipping info event:', error);
    }
  }

  /**
   * Push add payment info event
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushAddPaymentInfo() {
    try {
      return this.pushCheckoutFlow('dl_add_payment_info', '3');
    } catch (error) {
      console.error('Error pushing add payment info event:', error);
    }
  }

  /**
   * Push purchase event
   * @param {Object} options - Purchase options
   * @param {Object} options.orderData - Order details including ID, revenue, tax, etc.
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushPurchase({ orderData }) {
    try {
      if (!orderData) {
        throw new Error('Order data is required');
      }

      const formatCurrentOrderProducts = this.currentOrderProducts.map((cop) => ({
        ...cop,
        position: String(cop.position)
      }));

      const { cart_type = 'regular_purchase' } = orderData;

      return this.pushEvent('dl_purchase', {
        cart_type,
        ecommerce: {
          currencyCode: this.currencyCode,
          purchase: {
            actionField: { ...orderData },
            products: formatCurrentOrderProducts
          }
        },
        marketing: {
          landing_site: this.marketingLandingSite
        }
      });
    } catch (error) {
      console.error('Error pushing purchase event:', error);
    }
  }

  /**
   * Push view item event
   * @param {Object} options - View item options
   * @param {Object} options.item - Item being viewed
   * @param {string} options.referer - Referrer URL
   * @param {string} options.cart_type - Type of cart (default: 'main')
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushViewItem({ item, referer, cart_type = 'main' }) {
    try {
      if (!item) {
        throw new Error('Item is required');
      }

      const list = referer?.length ? referer : window.location.href;

      return this.pushEvent('dl_view_item', {
        cart_type,
        ecommerce: {
          currencyCode: this.currencyCode,
          detail: {
            actionField: {
              action: 'detail',
              list
            },
            products: [item]
          }
        }
      });
    } catch (error) {
      console.error('Error pushing view item event:', error);
    }
  }

  /**
   * Push view item list event
   * @param {Object} options - View item list options
   * @param {Array} options.impressions - List of product impressions
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushViewItemList({ impressions }) {
    try {
      if (!Array.isArray(impressions)) {
        throw new Error('Impressions must be an array');
      }

      return this.pushEvent('dl_view_item_list', {
        ecommerce: {
          currencyCode: this.currencyCode,
          impressions
        }
      });
    } catch (error) {
      console.error('Error pushing view item list event:', error);
    }
  }

  /**
   * Push add to cart event
   * @param {Object} options - Add to cart options
   * @param {string} options.cartType - Type of cart (default: 'main')
   * @param {Object} options.data - Response from /api/v2/storefront/cart/add_item
   * @param {boolean} options.isQuickView - Indicates if the add to cart was triggered via quick view
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushAddToCart({ cartType = 'main', data = {}, isQuickView = false }) {
    try {
      const list = (isQuickView || !data?.referer?.length) ? window.location.href : data.referer;

      return this.pushEvent('dl_add_to_cart', {
        cart_type: cartType,
        ecommerce: {
          currencyCode: this.currencyCode,
          add: {
            actionField: {
              list
            },
            products: data.items || []
          }
        }
      });
    } catch (error) {
      console.error('Error pushing add to cart event:', error);
    }
  }

  /**
   * Push remove from cart event
   * @param {Object} options - Remove from cart options
   * @param {Object} options.data - Response from /api/v2/storefront/cart/remove_line_item/{id}
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushRemoveFromCart({ data = {} }) {
    try {
      const list = data.referer || window.location.href;

      return this.pushEvent('dl_remove_from_cart', {
        ecommerce: {
          currencyCode: this.currencyCode,
          remove: {
            actionField: {
              list
            },
            products: data.items || []
          }
        }
      });
    } catch (error) {
      console.error('Error pushing remove from cart event:', error);
    }
  }

  /**
   * Push view cart event
   * @param {Object} options - View cart options
   * @param {Object} options.data - Cart data
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushViewCart({ data = {} }) {
    try {
      const cartTotal = data.cartTotal || '0';
      const cartType = data.cartType || 'main';
      const impressions = data.impressions || this.currentOrderProducts;

      return this.pushEvent('dl_view_cart', {
        cart_total: String(cartTotal),
        cart_type: cartType,
        ecommerce: {
          actionField: {
            list: 'Shopping Cart'
          },
          currencyCode: this.currencyCode,
          impressions
        }
      });
    } catch (error) {
      console.error('Error pushing view cart event:', error);
    }
  }

  /**
   * Push view search results event
   * @param {Object} options - View search results options
   * @param {Array} options.impressions - List of results impressions
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushViewSearchResults({ impressions }) {
    try {
      if (!Array.isArray(impressions)) {
        throw new Error('Impressions must be an array');
      }

      return this.pushEvent('dl_view_search_results', {
        ecommerce: {
          actionField: {
            list: 'Search Results'
          },
          currencyCode: this.currencyCode,
          impressions
        }
      });
    } catch (error) {
      console.error('Error pushing view search results event:', error);
    }
  }

  /**
   * Push select item event
   * @param {Object} options - Select item options
   * @param {Object} options.item - Selected item
   * @param {string} options.cart_type - Type of cart (default: 'main')
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushSelectItem({ item, cart_type = 'main' }) {
    try {
      if (!item) {
        throw new Error('Item is required');
      }

      const list = window.location.pathname;

      return this.pushEvent('dl_select_item', {
        cart_type,
        ecommerce: {
          click: {
            actionField: {
              action: 'click',
              list
            },
            products: [item]
          },
          currencyCode: this.currencyCode
        }
      });
    } catch (error) {
      console.error('Error pushing select item event:', error);
    }
  }

  /**
   * Push Sign Up event
   * @param {Object} signUpFlow - Sign up flow data
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushSignUp(signUpFlow) {
    try {
      return this.pushEvent('dl_sign_up', signUpFlow);
    } catch (error) {
      console.error('Error pushing sign up event:', error);
    }
  }

  /**
   * Push Sign In event
   * @param {Object} userProperties - User properties
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushSignIn(userProperties) {
    try {
      return this.pushEvent('dl_login', {
        user_properties: userProperties
      });
    } catch (error) {
      console.error('Error pushing sign in event:', error);
    }
  }

  /**
   * Push custom search results event
   * @param {Object} options - Search results options
   * @param {string} options.event - Event name
   * @param {Object} options.data - Event data
   * @returns {Object|undefined} The pushed event data or undefined if failed
   */
  pushCustomSearchResults({ event, data }) {
    try {
      return this.pushCustomEvent(event, data);
    } catch (error) {
      console.error('Error pushing custom search results event:', error);
    }
  }

}

// Safely expose the class to the window object
if (typeof window !== 'undefined') {
  window.InitElevarDataLayerHelper = InitElevarDataLayerHelper;
}
